import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/** Minimum trimmed query length before a predictive search request is made. */
const MIN_SEARCH_CHARS = 2;

/** Delay in ms between the last keystroke and the predictive search request. */
const SEARCH_DEBOUNCE_MS = 250;

/** Viewport width below which the header suggest panel becomes a fixed full-width sheet. */
const SUGGEST_MOBILE_QUERY = '(max-width: 749px)';

/** Gap in px between the header search field and the mobile suggest sheet. */
const SUGGEST_MOBILE_GAP = 6;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 *
 * Header "suggest" mode: when `data-mode="suggest"` is set on the element the component behaves as an inline
 * autocomplete dropdown (used by the always-visible header search bar) instead of the search modal. The results
 * container is shown/hidden with the `hidden` attribute, no empty state or recently viewed products are loaded, and
 * `data-resource-types` / `data-results-limit` are forwarded as `resources[type]` / `resources[limit]`.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  /**
   * Whether the component runs as the inline header autocomplete.
   * @returns {boolean}
   */
  get #isSuggest() {
    return this.dataset.mode === 'suggest';
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (this.#isSuggest) {
      this.#connectSuggest(signal);
      return;
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Wires the listeners only the header suggest mode needs.
   * @param {AbortSignal} signal - Aborted on disconnect.
   */
  #connectSuggest(signal) {
    const { predictiveSearchResults, searchInput } = this.refs;

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!predictiveSearchResults.hidden && event.target instanceof Node && !this.contains(event.target)) {
          this.closeResults();
        }
      },
      { signal }
    );

    // Defer one frame so focus has landed on the new element (for example a result link) before deciding.
    this.addEventListener(
      'focusout',
      () => {
        requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.closeResults();
        });
      },
      { signal }
    );

    // Keep focus in the input when pressing on non-link parts of the panel (padding, scrollbar).
    predictiveSearchResults.addEventListener(
      'mousedown',
      (event) => {
        if (event.target instanceof Element && !event.target.closest('a')) event.preventDefault();
      },
      { signal }
    );

    predictiveSearchResults.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('a')) this.closeResults();
      },
      { signal }
    );

    // Coming back through bfcache must not show a stale open panel.
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.closeResults();
      },
      { signal }
    );

    // Focusing a field that already holds a valid query reopens its results.
    searchInput.addEventListener(
      'focus',
      () => {
        if (predictiveSearchResults.childElementCount > 0 && searchInput.value.trim().length >= MIN_SEARCH_CHARS) {
          this.#openResults();
        }
      },
      { signal }
    );
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    // Suggest mode: options (product rows and the footer) are tagged with ref="resultsItems[]" in the markup.
    if (this.#isSuggest) {
      return /** @type {HTMLElement[]} */ (
        Array.from(this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]'))
      );
    }

    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    if (this.#isSuggest) {
      if (activeItem?.id) {
        this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
      } else {
        this.refs.searchInput.removeAttribute('aria-activedescendant');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // Suggest mode: first Escape closes the panel, a second one clears the field.
      if (this.#isSuggest && !this.refs.predictiveSearchResults.hidden) {
        this.closeResults();
        return;
      }
      this.#resetSearch();
      return;
    }

    // In suggest mode Tab leaves the field normally (focusout then closes the panel).
    if (this.#isSuggest && event.key === 'Tab') return;

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else if (this.#isSuggest) {
          event.preventDefault();
          this.refs.searchInput.form?.requestSubmit();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    // Too short to be useful: drop any in-flight request and keep what the user typed.
    if (searchTerm.length < MIN_SEARCH_CHARS) {
      this.#createAbortController();
      if (this.#isSuggest) this.closeResults();
      return;
    }

    this.#getSearchResults(searchTerm);
  }, SEARCH_DEBOUNCE_MS);

  /**
   * Hides the suggest panel, keeping the typed value. Suggest mode only.
   */
  closeResults = () => {
    if (!this.#isSuggest) return;

    const { predictiveSearchResults, searchInput } = this.refs;

    // Cancels any request still in flight so a late response cannot reopen the panel.
    this.#createAbortController();
    predictiveSearchResults.hidden = true;
    predictiveSearchResults.removeAttribute('aria-busy');
    searchInput.setAttribute('aria-expanded', 'false');
    searchInput.removeAttribute('aria-activedescendant');
    // Not the #currentIndex setter: it re-focuses the input, which a blur-driven close must not do.
    for (const item of this.#allResultsItems) {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    }
    this.#setBackdrop(false);
    this.#suggestAbort?.abort();
    this.#suggestAbort = null;
    document.body.classList.remove('hs-search-open');
  };

  /** @type {AbortController | null} */
  #suggestAbort = null;

  /**
   * Shows the suggest panel and positions the mobile sheet. Suggest mode only.
   */
  #openResults() {
    const { predictiveSearchResults, searchInput } = this.refs;

    predictiveSearchResults.hidden = false;
    searchInput.setAttribute('aria-expanded', 'true');
    this.#setBackdrop(true);

    if (this.#suggestAbort) return;

    this.#suggestAbort = new AbortController();
    const { signal } = this.#suggestAbort;
    const mobile = window.matchMedia(SUGGEST_MOBILE_QUERY);

    const update = () => {
      document.body.classList.toggle('hs-search-open', mobile.matches);
      const form = searchInput.form;
      if (!form) return;
      this.style.setProperty('--hs-top', `${Math.round(form.getBoundingClientRect().bottom + SUGGEST_MOBILE_GAP)}px`);
    };

    update();
    window.addEventListener('resize', update, { signal });
    window.visualViewport?.addEventListener('resize', update, { signal });
    window.visualViewport?.addEventListener('scroll', update, { signal });
    window.addEventListener('scroll', update, { signal, passive: true });
  }

  /**
   * Toggles the transparent outside-tap backdrop, if the markup provides one.
   * @param {boolean} visible - Whether the backdrop is shown.
   */
  #setBackdrop(visible) {
    const backdrop = this.querySelector('#HeaderSearchBackdrop');
    if (backdrop instanceof HTMLElement) backdrop.hidden = !visible;
  }

  /**
   * Wraps case-insensitive matches of the query words inside result titles in <mark class="hs-mark">.
   * Runs after every morph, which restores the unmarked server markup first.
   * @param {string} searchTerm - The query typed by the user.
   */
  #highlightMatches(searchTerm) {
    const words = searchTerm
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
      .sort((a, b) => b.length - a.length);
    if (!words.length) return;

    const pattern = new RegExp(`(${words.join('|')})`, 'gi');

    for (const title of this.refs.predictiveSearchResults.querySelectorAll('.hs-title')) {
      const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
      /** @type {Text[]} */
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(/** @type {Text} */ (walker.currentNode));

      for (const node of textNodes) {
        const text = node.nodeValue ?? '';
        if (!pattern.test(text)) continue;
        pattern.lastIndex = 0;

        const fragment = document.createDocumentFragment();
        for (const [index, part] of text.split(pattern).entries()) {
          if (!part) continue;
          if (index % 2 === 1) {
            const mark = document.createElement('mark');
            mark.className = 'hs-mark';
            mark.textContent = part;
            fragment.append(mark);
          } else {
            fragment.append(part);
          }
        }
        node.replaceWith(fragment);
      }
    }
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const scroller = this.#isSuggest ? '.hs-list' : '.predictive-search-results__inner';
      this.refs.predictiveSearchResults.querySelector(scroller)?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    // Optional per-instance scoping, e.g. the header suggest only wants products.
    if (this.dataset.resourceTypes) url.searchParams.set('resources[type]', this.dataset.resourceTypes);
    if (this.dataset.resultsLimit) url.searchParams.set('resources[limit]', this.dataset.resultsLimit);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    if (this.#isSuggest) this.#prepareSuggestLoading();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        if (this.#isSuggest) this.#highlightMatches(searchTerm);

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      })
      .finally(() => {
        // Only the newest request may clear the busy state.
        if (this.#isSuggest && this.#activeFetch === abortController) {
          predictiveSearchResults.removeAttribute('aria-busy');
        }
      });
  }

  /**
   * Opens the suggest panel and marks it busy. The skeleton is only injected while the panel is empty (first open);
   * later requests keep the previous results on screen, dimmed by the `[aria-busy]` styles.
   */
  #prepareSuggestLoading() {
    const { predictiveSearchResults } = this.refs;

    if (predictiveSearchResults.childElementCount === 0) {
      const skeleton = this.querySelector('#HeaderSearchSkeleton');
      if (skeleton instanceof HTMLTemplateElement) {
        predictiveSearchResults.append(skeleton.content.cloneNode(true));
      }
    }

    // The skeleton footer already shows the typed query; harmless no-op when the real results are on screen.
    const query = this.refs.searchInput.value.trim();
    const footer = predictiveSearchResults.querySelector('#HeaderSearchFooterSkeleton');
    if (footer instanceof HTMLAnchorElement) {
      footer.href = `${Theme.routes.search_url}?type=product&q=${encodeURIComponent(query)}`;
      const queryNode = footer.querySelector('.hs-footer-link__query');
      if (queryNode) queryNode.textContent = query;
    }

    predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.#openResults();
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    // Suggest mode has no empty state: clearing means empty field, no results, closed panel.
    if (this.#isSuggest) {
      searchInput.value = '';
      this.#hideResetButton();
      this.closeResults();
      predictiveSearchResults.replaceChildren();
      return;
    }

    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
